import type { AgentControlDefinition, AgentControlFunctionInfo } from "./types";

import { createShallowStore } from "replo-utils/react/zustand";
import { ReploError } from "schemas/reploError";

export class AgentControlError extends ReploError {
  constructor(message: string) {
    super({ message });
  }
}

type AgentControlImplementation = {
  fn: (...args: unknown[]) => unknown;
};

type AgentControlState = {
  definitions: Record<string, AgentControlDefinition>;
  implementations: Record<string, AgentControlImplementation>;
  actions: {
    registerDefinitions: (defs: AgentControlDefinition[]) => void;
    unregisterDefinitions: (names: string[]) => void;
    registerFunction: (
      name: string,
      fn: (...args: unknown[]) => unknown,
    ) => void;
    unregisterFunction: (name: string) => void;
  };
};

export const useAgentControlStore = createShallowStore<AgentControlState>(
  (set) => ({
    definitions: {},
    implementations: {},
    actions: {
      registerDefinitions: (defs) => {
        return set((state) => {
          const next = { ...state.definitions };
          for (const def of defs) {
            next[def.name] = def;
          }
          return { definitions: next };
        });
      },
      unregisterDefinitions: (names) => {
        return set((state) => {
          const next = { ...state.definitions };
          for (const name of names) {
            delete next[name];
          }
          return { definitions: next };
        });
      },
      registerFunction: (name, fn) => {
        return set((state) => ({
          implementations: { ...state.implementations, [name]: { fn } },
        }));
      },
      unregisterFunction: (name) => {
        return set((state) => {
          const next = { ...state.implementations };
          delete next[name];
          return { implementations: next };
        });
      },
    },
  }),
);

function buildCallSignature(def: AgentControlDefinition): string {
  const paramNames = def.params ? Object.keys(def.params) : [];
  return `window._reploAgentControl.${def.name}(${paramNames.join(", ")})`;
}

export function listAgentControlFunctions(): AgentControlFunctionInfo[] {
  const { definitions, implementations } = useAgentControlStore.getState();
  return Object.values(definitions).map((def) => {
    const impl = implementations[def.name];
    const callSignature = buildCallSignature(def);
    return {
      name: def.name,
      description: `${def.description}\nUsage: ${callSignature}`,
      params: def.params,
      enabled: Boolean(impl),
      message: impl
        ? undefined
        : `Not enabled because the component that provides "${def.name}" is not mounted`,
    };
  });
}

export function callAgentControlFunction(
  name: string,
  ...args: unknown[]
): unknown {
  const { definitions, implementations } = useAgentControlStore.getState();
  const def = definitions[name];
  if (!def) {
    throw new AgentControlError(
      `Unknown agent control function: "${name}". Call listFunctions() to see available functions.`,
    );
  }
  const impl = implementations[name];
  if (!impl) {
    throw new AgentControlError(
      `Agent control function "${name}" is defined but not currently enabled. The component that provides it is not mounted.`,
    );
  }
  return impl.fn(...args);
}
